import type { Session } from '@supabase/supabase-js'
import { writeCookie } from '@/lib/cookies'
import type { CurrentUserProfile } from '@/types'
import { IMPERS_STASH_COOKIE } from '../lib/storage-keys'
import type { ImpersonationAdminStash } from '../lib/types'

/**
 * Inicia impersonação:
 *  1. Chama /api/start-impersonation (gera magic link via service role)
 *  2. Stash admin tokens em cookie .runnaflow.com (cross-subdomain)
 *  3. Abre nova aba com ?impersonate=TOKEN — handshake roda no AuthContext
 *
 * NÃO mexe na sessão da aba atual — admin continua logado aqui.
 */
export async function startImpersonation(params: {
  targetUserId: string
  profile: CurrentUserProfile
  session: Session
}): Promise<void> {
  const { targetUserId, profile, session } = params

  if (!profile.is_platform_admin) throw new Error('Somente platform admin pode impersonar')
  if (!session.access_token || !session.refresh_token) {
    throw new Error('Sessão admin ausente ou incompleta')
  }

  // 1. Gera hashed_token magic-link do target (via service role no endpoint)
  const res = await fetch('/api/start-impersonation', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ target_user_id: targetUserId }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(err.detail || `HTTP ${res.status}`)
  }
  const { hashed_token } = await res.json()

  // 2. Stash em cookie .runnaflow.com (cross-subdomain). A nova aba lê esse
  //    cookie pra saber que está impersonando e exibir o banner.
  //    SEM refresh_token: cookie legível por JS + refresh_token = takeover de
  //    admin via XSS. O "Encerrar" só fecha a aba — nunca precisou dele.
  const stash: ImpersonationAdminStash = {
    admin_access_token:  session.access_token,
    admin_name:  profile.user.name,
    admin_email: profile.user.email,
    admin_user_id: profile.user.id,
    started_at: new Date().toISOString(),
  }
  writeCookie(IMPERS_STASH_COOKIE, JSON.stringify(stash))

  // 3. Abre nova aba com ?impersonate=TOKEN. MESMO PADRÃO do god mode
  //    (enterAdminTenant abre ?tenant=). NÃO mexe na sessão da aba atual —
  //    admin continua logado aqui. Nova aba roda o handshake no useEffect
  //    do AuthContext: verifyOtp → sessão target → banner "impersonado por".
  const host = window.location.hostname
  const appOrigin =
    host === 'admin.runnaflow.com'     ? 'https://runnaflow.com' :
    host === 'admin-dev.runnaflow.com' ? 'https://runnaflow-dev.runnaflow.com' :
    window.location.origin
  window.open(`${appOrigin}/?impersonate=${encodeURIComponent(hashed_token)}`, '_blank')
}
